// @ts-nocheck
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Plus, Filter, MoreVertical, Edit, Trash2,
    Users, Phone,
    ArrowRight, Grid, List, X, Mail as MailIcon,
    ChevronDown, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useCustomers, useDeleteCustomer, getCustomerGroupsRes } from 'core';
import { SkeletonSwitch } from 'skeleton';
import { LeadCardGridSkeleton, LeadCardListSkeleton } from 'skeleton/leads/LeadsSkeleton';
import Pagination from 'components/ui/pagination';
import ConfirmationDialog from 'components/ui/confirmationDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import LeadForm from 'components/leads/leads-form/LeadsForm';
import { useSearchParams, useNavigate } from "react-router-dom";
import LeadsImport from './leads-import/LeadsImport';
import { PageContainer } from '../ui/page-container';
import LeadsHeader from './LeadsHeader';
import { EmptyState } from "components/ui/empty-state";
import { ErrorState } from "components/ui/error-state";


const PortalMenu = ({ triggerRef, isOpen, onClose, children }) => {
    const [pos, setPos] = useState(null);
    useEffect(() => {
        if (!isOpen) { setPos(null); return; }
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const spaceBelow = window.innerHeight - rect.bottom;
        setPos(
            spaceBelow >= 160
                ? { top: rect.bottom + 4, right: window.innerWidth - rect.right }
                : { bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right }
        );
    }, [isOpen]);

    useEffect(() => {
        if (!pos) return;
        const close = () => onClose();
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [pos, onClose]);
    if (!isOpen || !pos) return null;
    return createPortal(
        <>
            <div className="fixed inset-0 z-[9990]" onClick={(e) => { e.stopPropagation(); onClose(); }} />
            <div
                className="fixed z-[9991] bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl shadow-xl min-w-[140px] py-1 overflow-hidden"
                style={{ top: pos.top ?? 'auto', bottom: pos.bottom ?? 'auto', right: pos.right, animation: 'fadeIn 0.12s ease both' }}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </>,
        document.body
    );
};


const ITEMS_PER_PAGE = 50;

const LEAD_LIST_GRID_CLASS = `grid items-center gap-x-4 gap-y-2 px-4
    grid-cols-[auto_auto_minmax(0,1fr)_auto]
    sm:grid-cols-[auto_auto_minmax(180px,1.5fr)_minmax(120px,1fr)_minmax(120px,1fr)_48px]
    md:grid-cols-[auto_auto_minmax(220px,1.5fr)_minmax(140px,1fr)_minmax(140px,1fr)_48px]
    xl:grid-cols-[auto_auto_minmax(260px,1.5fr)_minmax(160px,1fr)_minmax(160px,1fr)_48px]
    xl:gap-x-6`;

const EnhancedLeadsView = ({
    leads = [],
    segments = [],
    onCreateSegmentFromSelection,
    onAddToSegment,
    onEditLead,
    onDeleteLead,
    onEmailLead,
    /* No `onAddLead`: adding a lead is this view's own business now. It owns
       the dialog, and the only caller that passed one was a console.log stub in
       a page nothing routes to. */
    selectedSegmentFilter = 'all',
    onSegmentFilterChange,
    onReturnToAudiences
}) => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [scoreFilter, setScoreFilter] = useState('all');
    const [sourceFilter, setSourceFilter] = useState('all');
    const [sortBy, setSortBy] = useState('lastEngaged');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedLeads, setSelectedLeads] = useState(new Set());
    const [viewMode, setViewMode] = useState('list');
    const [currentPage, setCurrentPage] = useState(1);
    const [showSegmentFilter, setShowSegmentFilter] = useState(false);
    const [showAddToSegmentModal, setShowAddToSegmentModal] = useState(false);
    const [customerConfirmationOpen, setCustomerConfirmationOpen] = useState(false);
    const [openGroupFilter, setOpenGroupFilter] = useState(false);

    const defaultCustomersFilters = () => {
        const v = searchParams.get("groupId");
        const n = Number(v);
        return v && !Number.isNaN(n) ? { page: 1, pageSize: 10, groupId: n } : { page: 1, pageSize: 10 };
    }

    const [customerFilters, setCustomersFilters] = useState(defaultCustomersFilters());

    /*
     * Whether the server-side query is narrowed. This is what separates "you
     * have no contacts" from "your filters matched nothing" — two situations
     * that need opposite answers, and that the page previously conflated.
     */
    const hasActiveLeadFilters = Boolean(
        (customerFilters as any)?.search || (customerFilters as any)?.groupId
    );

    const clearLeadFilters = () => {
        setSearchTerm('');
        setCustomersFilters({ page: 1, pageSize: 10 });
    };
    const [selectedCustomer, setSelectedCustomer] = useState(null)
    const [allCustomersGroups, setAllCustomersGroups] = useState()

    // Debounce searchTerm → push into customerFilters so the API query picks it up
    const searchDebounceRef = useRef(null);
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCustomersFilters((prev) => ({
                ...prev,
                page: 1,
                search: value.trim(),
            }));
        }, 350);
    };

    const {
        data: customerData,
        isFetching: customerLoading,
        isError: customerError,
        error: customerErrorObj,
        refetch: refetchCustomers,
    } = useCustomers(customerFilters);
    const { mutate: deleteCustomer } = useDeleteCustomer();

    const kpiMetrics = useMemo(() => {
        const totalLeads = customerData?.total || 0;
        const activeLeads = customerData?.data?.filter(l => l.status === 'valid').length || 0;
        const totalCustomers = customerData?.data?.length || 0;
        return { totalLeads, hotLeads: 0, highScoreLeads: 0, recentActivityLeads: activeLeads };
    }, [customerData]);

    /* Adding a lead is done over the table rather than by navigating to
       /leads/new. The list is usually filtered, searched and paged when the
       button is pressed, and a route change threw all of that away — the form
       even carried `navigate("/leads?page=" + currentPage)` to put one piece of
       it back. `LeadsForm` already supports this: Widgets renders the same
       component in a dialog. The route still exists for deep links. */
    const [addingLead, setAddingLead] = useState(false);

    useEffect(() => { getAllCustomersGroups() }, [])

    const getAllCustomersGroups = async () => {
        try {
            const res = await getCustomerGroupsRes();
            setAllCustomersGroups(res)
        } catch (err) {
            console.log("Error while fetching all groups", err)
        }
    }

    const isLeadInSegment = useCallback((lead, segment) => {
        if (segment.manualLeadIds && segment.manualLeadIds.includes(lead.id)) return true;
        if (!segment.criteria || segment.criteria.length === 0) return false;
        const andGroups = [];
        let currentAndGroup = [];
        segment.criteria.forEach(criterion => {
            if (criterion.logicalOperator === 'OR') {
                if (currentAndGroup.length > 0) andGroups.push(currentAndGroup);
                currentAndGroup = [criterion];
            } else {
                currentAndGroup.push(criterion);
            }
        });
        if (currentAndGroup.length > 0) andGroups.push(currentAndGroup);
        return andGroups.some(andGroup => andGroup.every(criterion => {
            const leadValue = lead[criterion.field];
            switch (criterion.operator) {
                case 'equals': return leadValue === criterion.value;
                case 'greater_than': return criterion.field === 'leadScore' ? leadValue > criterion.value : new Date(leadValue) > new Date(criterion.value);
                case 'less_than': return criterion.field === 'leadScore' ? leadValue < criterion.value : new Date(leadValue) < new Date(criterion.value);
                case 'contains': return leadValue && leadValue.toString().toLowerCase().includes(criterion.value.toLowerCase());
                default: return false;
            }
        }));
    }, []);

    const getLeadSegments = useCallback((lead) => segments.filter(segment => isLeadInSegment(lead, segment)), [segments, isLeadInSegment]);

    const filteredAndSortedLeads = useMemo(() => {
        let filtered = leads;
        if (selectedSegmentFilter !== 'all') {
            const segment = segments.find(s => s.id === selectedSegmentFilter);
            if (segment) filtered = filtered.filter(lead => isLeadInSegment(lead, segment));
        }
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(lead =>
                lead.name.toLowerCase().includes(searchLower) ||
                lead.email.toLowerCase().includes(searchLower) ||
                lead.source.toLowerCase().includes(searchLower) ||
                lead.location.toLowerCase().includes(searchLower) ||
                lead.tags.some(tag => tag.toLowerCase().includes(searchLower))
            );
        }
        if (statusFilter !== 'all') filtered = filtered.filter(lead => lead.status === statusFilter);
        if (scoreFilter !== 'all') filtered = filtered.filter(lead => {
            switch (scoreFilter) {
                case 'high': return lead.leadScore >= 80;
                case 'medium': return lead.leadScore >= 50 && lead.leadScore < 80;
                case 'low': return lead.leadScore < 50;
                default: return true;
            }
        });
        if (sourceFilter !== 'all') filtered = filtered.filter(lead => lead.source === sourceFilter);
        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (sortBy) {
                case 'name': aValue = a.name.toLowerCase(); bValue = b.name.toLowerCase(); break;
                case 'leadScore': aValue = a.leadScore || 0; bValue = b.leadScore || 0; break;
                case 'lastEngaged': aValue = new Date(a.lastEngaged); bValue = new Date(b.lastEngaged); break;
                case 'status': const statusOrder = { hot: 3, warm: 2, cold: 1 }; aValue = statusOrder[a.status] || 0; bValue = statusOrder[b.status] || 0; break;
                default: return 0;
            }
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [leads, segments, selectedSegmentFilter, searchTerm, statusFilter, scoreFilter, sourceFilter, sortBy, sortOrder, isLeadInSegment]);

    const totalPages = 0;
    const paginatedLeads = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredAndSortedLeads.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredAndSortedLeads, currentPage]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, scoreFilter, sourceFilter, selectedSegmentFilter]);

    const getCurrentSegmentName = () => {
        if (selectedSegmentFilter === 'all') return null;
        const segment = segments.find(s => s.id === selectedSegmentFilter);
        return segment ? segment.name : null;
    };

    const handleLeadSelect = (leadId) => {
        const newSelected = new Set(selectedLeads);
        if (newSelected.has(leadId)) newSelected.delete(leadId);
        else newSelected.add(leadId);
        setSelectedLeads(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedLeads.size === paginatedLeads.length) setSelectedLeads(new Set());
        else setSelectedLeads(new Set(paginatedLeads.map(lead => lead.id)));
    };

    const clearSelection = () => setSelectedLeads(new Set());

    const handleCreateSegmentFromSelection = async () => {
        const selectedLeadIds = Array.from(selectedLeads);
        if (selectedLeadIds.length === 0) return;
        try { await onCreateSegmentFromSelection(selectedLeadIds); clearSelection(); } catch (error) { console.error('Failed to create segment:', error); }
    };

    const handleAddToSegment = () => {
        if (Array.from(selectedLeads).length === 0) return;
        setShowAddToSegmentModal(true);
    };

    const handleAddToSpecificSegment = async (segmentId) => {
        const selectedLeadIds = Array.from(selectedLeads);
        try { await onAddToSegment(selectedLeadIds, segmentId); clearSelection(); setShowAddToSegmentModal(false); }
        catch (error) { console.error('Failed to add leads to segment:', error); setShowAddToSegmentModal(false); }
    };

    return (
        <PageContainer>
            <LeadsHeader
                totalLeads={kpiMetrics.totalLeads}
                hotLeads={kpiMetrics.hotLeads}
                highScoreLeads={kpiMetrics.highScoreLeads}
                recentActivityLeads={kpiMetrics.recentActivityLeads}
                shownCount={customerData?.data?.length || 0}
                onAddLead={() => setAddingLead(true)}
                isLoading={customerLoading}
                LeadsImportComponent={<LeadsImport updateCustomerGroup={getAllCustomersGroups} />}
                selectedSegmentName={getCurrentSegmentName()}
                onReturnToAudiences={onReturnToAudiences}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
            />

            {/* Bulk selection banner */}
            {selectedLeads.size > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-xl p-3.5 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between w-full max-w-full min-w-0 overflow-hidden">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                        {selectedLeads.size} lead{selectedLeads.size !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                        <button onClick={handleCreateSegmentFromSelection} className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">
                            <Users className="w-3.5 h-3.5" />Create Segment
                        </button>
                        <button onClick={handleAddToSegment} className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">
                            <Plus className="w-3.5 h-3.5" />Add to Segment
                        </button>
                    </div>
                </div>
            )}

            {/* ── Filter Toolbar ── */}
            <div className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl shadow-sm p-4 mb-5">
                <div className="flex flex-wrap items-center gap-2">
                    {/* Customer Group dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setOpenGroupFilter((v) => !v)}
                            className={`flex items-center gap-2 px-3 py-2 text-[13px] border rounded-lg transition-colors font-medium ${customerFilters.groupId != null
                                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300"
                                : "border-line dark:border-dark-600 bg-surface dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600"
                                }`}
                        >
                            <Users className="w-3.5 h-3.5" />
                            {customerFilters.groupId != null ? (allCustomersGroups?.find(g => g.id === customerFilters.groupId)?.name ?? "Group") : "Group"}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openGroupFilter ? "rotate-180" : ""}`} />
                        </button>
                        {openGroupFilter && (
                            <div className="absolute left-0 mt-1.5 w-56 bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl shadow-xl z-20 py-1">
                                <button
                                    onClick={() => { setCustomersFilters((p) => ({ ...p, groupId: undefined, page: 1 })); setOpenGroupFilter(false); }}
                                    className={`w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-between ${customerFilters.groupId == null ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                                >
                                    <span>All Groups</span>
                                    <span className="text-[11px] text-content-subtle">({(allCustomersGroups ?? []).reduce((s, g) => s + (g.leadsCount ?? 0), 0)})</span>
                                </button>
                                {(allCustomersGroups ?? []).map((g) => (
                                    <button
                                        key={g.id}
                                        onClick={() => { setCustomersFilters((p) => ({ ...p, groupId: g.id, page: 1 })); setOpenGroupFilter(false); }}
                                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-between ${customerFilters.groupId === g.id ? "text-blue-600 dark:text-blue-400 font-medium" : "text-gray-700 dark:text-gray-300"}`}
                                    >
                                        <span>{g.name}</span>
                                        <span className="text-[11px] text-content-subtle">({g.leadsCount ?? 0})</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Segment dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowSegmentFilter(!showSegmentFilter)}
                            className={`flex items-center gap-2 px-3 py-2 text-[13px] border rounded-lg transition-colors font-medium ${selectedSegmentFilter !== 'all'
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                                : 'border-line dark:border-dark-600 bg-surface dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600'
                                }`}
                        >
                            <Filter className="w-3.5 h-3.5" />
                            Segment
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSegmentFilter ? 'rotate-180' : ''}`} />
                        </button>
                        {showSegmentFilter && (
                            <div className="absolute left-0 mt-1.5 w-56 bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-xl shadow-xl z-20 py-1">
                                <button
                                    onClick={() => { onSegmentFilterChange('all'); setShowSegmentFilter(false); }}
                                    className={`w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-between ${selectedSegmentFilter === 'all' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                                >
                                    All Leads ({leads.length})
                                </button>
                                {segments.map(segment => (
                                    <button
                                        key={segment.id}
                                        onClick={() => { onSegmentFilterChange(segment.id); setShowSegmentFilter(false); }}
                                        className={`w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-between ${selectedSegmentFilter === segment.id ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                                    >
                                        <span>{segment.name}</span>
                                        <span className="text-[11px] text-content-subtle">({leads.filter(lead => isLeadInSegment(lead, segment)).length})</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Row controls: select-all + pagination + view toggle */}
            {customerData?.data?.length > 0 && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={selectedLeads.size === paginatedLeads.length && paginatedLeads.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-blue-500/30"
                        />
                        <span className="text-[13px] text-content-secondary dark:text-gray-400">
                            Select all {customerData?.data?.length || 0} on page
                        </span>
                    </label>

                    <div className="flex items-center gap-2">
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                                    className="p-1.5 border border-line dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 transition-colors">
                                    <ChevronLeft className="w-3.5 h-3.5 text-content-secondary dark:text-gray-300" />
                                </button>
                                <span className="text-[12px] text-content-secondary dark:text-gray-400 px-2">{currentPage}/{totalPages}</span>
                                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                                    className="p-1.5 border border-line dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 transition-colors">
                                    <ChevronRight className="w-3.5 h-3.5 text-content-secondary dark:text-gray-300" />
                                </button>
                            </div>
                        )}

                        {/* View mode toggle */}
                        <div className="flex items-center bg-gray-100 dark:bg-dark-700 border border-line dark:border-dark-600 rounded-lg p-0.5">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-surface dark:bg-dark-800 text-blue-600 shadow-sm' : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                aria-label="Grid view"
                            >
                                <Grid className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-surface dark:bg-dark-800 text-blue-600 shadow-sm' : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                aria-label="List view"
                            >
                                <List className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lead cards */}
            <SkeletonSwitch
                isLoading={customerLoading}
                skeleton={viewMode === 'grid' ? <LeadCardGridSkeleton key={5} /> : <LeadCardListSkeleton />}
            >
                {customerError ? (
                    /* A failed request used to render the empty state, telling
                       the user they had no contacts when the server had simply
                       not answered. */
                    <ErrorState
                        bare
                        title="Couldn't load your leads"
                        description="The request didn't complete. Your contacts are safe — this is only the view."
                        error={customerErrorObj}
                        onRetry={() => refetchCustomers()}
                    />
                ) : customerData?.data?.length === 0 ? (
                    /* "No leads at all" and "your filters matched nothing" need
                       different answers: one offers a way to add, the other a
                       way back. Previously both showed "No leads yet" with no
                       action at all. */
                    hasActiveLeadFilters ? (
                        <EmptyState
                            bare
                            variant="no-results"
                            title="No leads match your filters"
                            description="Nothing in your list fits the current search and group. Widen them to see more."
                            primaryAction={{ label: "Clear filters", onClick: clearLeadFilters }}
                        />
                    ) : (
                        <EmptyState
                            bare
                            icon={Users}
                            title="No leads yet"
                            description="Add a contact by hand, or import a list to get started."
                            primaryAction={{
                                label: "Add lead",
                                onClick: () => navigate("/leads/new"),
                                icon: <Plus className="w-4 h-4" />,
                            }}
                        />
                    )
                ) : (
                    <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-2'}`}>
                        {viewMode === 'list' && (
                            <div className={`${LEAD_LIST_GRID_CLASS} hidden sm:grid pb-1 text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500`}>
                                <span aria-hidden="true" />
                                <span aria-hidden="true" />
                                <span>Lead</span>
                                <span className="text-center">Group</span>
                                <span className="text-center">Phone</span>
                                <span className="justify-self-center">Actions</span>
                            </div>
                        )}
                        {customerData?.data?.map((lead) => (
                            <LeadCard
                                key={lead.id}
                                lead={lead}
                                segments={getLeadSegments(lead)}
                                isSelected={selectedLeads.has(lead.id)}
                                onSelect={() => handleLeadSelect(lead.id)}
                                onEdit={() => onEditLead(lead)}
                                onDelete={() => onDeleteLead(lead.id)}
                                onEmail={() => onEmailLead(lead)}
                                viewMode={viewMode}
                                setCustomerConfirmationOpen={setCustomerConfirmationOpen}
                                setSelectedCustomer={setSelectedCustomer}
                            />
                        ))}
                    </div>
                )}
            </SkeletonSwitch>

            {customerData?.total > 0 && !customerLoading && (
                <Pagination
                    totalEntries={customerData?.total}
                    entriesPerPage={customerData?.limit}
                    currentPage={customerFilters.page}
                    onPageChange={(page) => { if (!customerLoading) setCustomersFilters((prev) => ({ ...prev, page })); }}
                    showSummary
                    maxVisiblePages={5}
                />
            )}

            {/* Add to Segment Modal */}
            {showAddToSegmentModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center z-50">
                    <div className="bg-surface dark:bg-dark-800 border border-line dark:border-dark-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                        <div className="h-[3px] bg-gradient-to-r from-blue-500 to-indigo-500" />
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-[16px] font-bold text-content dark:text-white">
                                    Add {selectedLeads.size} Lead{selectedLeads.size !== 1 ? 's' : ''} to Segment
                                </h3>
                                <button onClick={() => setShowAddToSegmentModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 text-content-subtle dark:text-gray-500 hover:text-content-secondary dark:hover:text-gray-300 transition-colors">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-[13px] text-content-secondary dark:text-gray-400 mb-4">Choose a segment to add the selected leads to:</p>
                            {segments.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-dark-700 flex items-center justify-center mx-auto mb-3">
                                        <Users className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                                    </div>
                                    <p className="text-[13px] text-content-muted dark:text-gray-400">No segments available. Create one first.</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-60 overflow-y-auto no-scrollbar">
                                    {segments.map(segment => (
                                        <button key={segment.id} onClick={() => handleAddToSpecificSegment(segment.id)}
                                            className="w-full text-left px-4 py-3 border border-line dark:border-dark-700 bg-gray-50 dark:bg-dark-700/60 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-200 dark:hover:border-blue-800/50 rounded-xl transition-all">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-[13px] font-semibold text-content dark:text-white">{segment.name}</h4>
                                                    <p className="text-[12px] text-content-muted dark:text-gray-400">{segment.size || segment.count || 0} leads</p>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-content-subtle dark:text-gray-500" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="flex justify-end mt-5 pt-4 border-t border-line-subtle dark:border-dark-700">
                                <button onClick={() => setShowAddToSegmentModal(false)}
                                    className="text-[13px] font-medium px-4 py-2 rounded-xl border border-line dark:border-dark-600 bg-surface dark:bg-dark-700 text-content-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationDialog
                open={customerConfirmationOpen}
                title="Delete this lead?"
                description="This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={async () => { deleteCustomer(selectedCustomer); setCustomerConfirmationOpen(false); }}
                onCancel={() => setCustomerConfirmationOpen(false)}
            />

            <Dialog open={addingLead} onOpenChange={(open) => !open && setAddingLead(false)}>
                <DialogContent size="lg" padding={false}>
                    {/* The form draws its own "Create New Lead" heading, so this is
                        the dialog's accessible name only — a second visible title
                        on top of it was just the same words twice. */}
                    <DialogHeader className="sr-only">
                        <DialogTitle>Create new lead</DialogTitle>
                    </DialogHeader>
                    <LeadForm
                        isModal
                        onClose={() => setAddingLead(false)}
                        onSave={() => {
                            // The list the user is looking at has to show it.
                            void refetchCustomers();
                            void getAllCustomersGroups();
                        }}
                    />
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

const LeadCard = ({ lead, isSelected, onSelect, onEdit, onDelete, onEmail, viewMode = "grid", setCustomerConfirmationOpen, setSelectedCustomer }) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuBtnRef = useRef(null);
    const closeMenu = useCallback(() => setShowMenu(false), []);
    const navigate = useNavigate();

    const name = [lead?.firstName, lead?.lastName].filter(Boolean).join(" ").trim() || "—";
    const email = lead?.email || "—";
    const status = lead?.status?.toLowerCase() || "unknown";
    const groupName = lead?.group?.name || "No Group";
    const phoneNumber = lead?.phoneNumber || null;
    const whatsappNumber = lead?.whatsappNumber || null;

    const statusDot = {
        hot: "bg-red-500", warm: "bg-orange-400", cold: "bg-blue-500",
        valid: "bg-emerald-500", unknown: "bg-gray-300"
    }[status] || "bg-gray-400";

    const statusBadge = {
        hot: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
        warm: "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
        cold: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
        valid: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400",
        unknown: "bg-gray-100 dark:bg-dark-600 text-content-muted dark:text-gray-400"
    }[status] || "bg-gray-100 dark:bg-dark-600 text-content-secondary dark:text-gray-400";

    // ── LIST VIEW ──
    if (viewMode === "list") {
        return (
            <div className={`bg-surface dark:bg-dark-800 border rounded-xl transition-all ${isSelected ? "border-blue-500 ring-2 ring-blue-500/15 shadow-sm" : "border-line dark:border-dark-700 hover:border-line-strong dark:hover:border-dark-600 hover:shadow-sm"
                }`}>
                <div className={`${LEAD_LIST_GRID_CLASS} py-3`}>
                    <input type="checkbox" checked={isSelected} onChange={onSelect}
                        className="rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-blue-500/30 flex-shrink-0" />
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} />
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap sm:flex-nowrap">
                            <span className="min-w-0 text-ui font-semibold text-content dark:text-white truncate" title={name}>{name}</span>
                            <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase flex-shrink-0 ${statusBadge}`}>{status}</span>
                        </div>
                        <p className="text-meta text-content-muted dark:text-gray-400 truncate" title={email}>{email}</p>
                    </div>

                    <div className="hidden sm:block min-w-0">
                        <div className="text-meta font-medium text-content dark:text-white truncate text-center" title={groupName}>{groupName}</div>
                    </div>
                    <div className="hidden sm:block min-w-0">
                        <div className="text-meta text-gray-700 dark:text-gray-300 truncate text-center" title={phoneNumber || whatsappNumber || "—"}>
                            {phoneNumber || whatsappNumber || "—"}
                        </div>
                    </div>
                    {/* 3-dot menu */}
                    <div className="relative justify-self-center">
                        <button
                            ref={menuBtnRef}
                            onClick={(e) => { e.stopPropagation(); setShowMenu(p => !p); }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 text-content-subtle dark:text-gray-500 hover:text-content-secondary dark:hover:text-gray-300 transition-colors"
                            aria-label="More options"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>
                        <PortalMenu triggerRef={menuBtnRef} isOpen={showMenu} onClose={closeMenu}>
                            <button onClick={() => { navigate(`/leads/${lead?.groupId}/${lead?.group?.name}/${lead?.id}`); onEdit(); closeMenu(); }}
                                className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <Edit className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button onClick={() => { onEmail(); closeMenu(); }}
                                className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <MailIcon className="w-3.5 h-3.5" /> Email
                            </button>
                            <button onClick={() => { onDelete(); closeMenu(); }}
                                className="w-full text-left px-3 py-2 text-[13px] hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 text-red-600 dark:text-red-400">
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                        </PortalMenu>
                    </div>

                    <div className="col-start-3 col-span-2 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 min-w-0 text-[11px] font-medium text-content-muted dark:text-gray-400 sm:hidden">
                        <span className="min-w-0 truncate" title={groupName}>{groupName}</span>
                        <span className="whitespace-nowrap text-gray-700 dark:text-gray-300">{phoneNumber || whatsappNumber || "No phone"}</span>
                    </div>
                </div>
            </div>
        );
    }

    // -- GRID VIEW --
    const avatarGradients = [
        'from-blue-500 to-indigo-600',
        'from-violet-500 to-purple-600',
        'from-emerald-500 to-teal-600',
        'from-rose-500 to-pink-600',
        'from-amber-500 to-orange-600',
        'from-cyan-500 to-sky-600',
    ];
    const avatarGradient = avatarGradients[(name.charCodeAt(0) || 0) % avatarGradients.length];

    const statusAccentBar = {
        hot: 'from-red-500 to-orange-500',
        warm: 'from-orange-500 to-amber-400',
        cold: 'from-blue-500 to-cyan-500',
        valid: 'from-emerald-500 to-teal-500',
        unknown: 'from-gray-400 to-gray-500',
    }[status] || 'from-blue-500 to-indigo-500';

    return (
        <div className={`group flex flex-col bg-surface dark:bg-dark-800 border rounded-2xl overflow-hidden transition-all duration-200 ${isSelected
            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-lg shadow-blue-500/10'
            : 'border-line dark:border-dark-700 hover:border-line-strong dark:hover:border-dark-600 hover:shadow-xl hover:shadow-black/5 dark:hover:shadow-black/20'
            }`}>

            {/* Top status accent bar */}
            <div className={`h-[3px] w-full bg-gradient-to-r ${statusAccentBar} flex-shrink-0`} />

            {/* Card Body */}
            <div className="flex flex-col flex-1 p-4 gap-3">

                {/* Row 1: checkbox + avatar + name/email + status badge */}
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={onSelect}
                            className="rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-blue-500/30 flex-shrink-0 mt-1 cursor-pointer"
                        />
                        <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${avatarGradient} flex items-center justify-center text-white text-[15px] font-bold shadow-sm ring-2 ring-white dark:ring-dark-800`}>
                            {name !== "—" ? name.charAt(0).toUpperCase() : "?"}
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                            <h4 className="text-ui-lg font-bold text-content dark:text-white leading-snug truncate">{name}</h4>
                            <p className="text-meta text-content-muted dark:text-gray-300 truncate mt-0.5">{email}</p>
                        </div>
                    </div>
                    {/* Status Badge moved to top right */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusBadge} flex-shrink-0 mt-0.5`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot} flex-shrink-0`} />
                        {status}
                    </span>
                </div>

                {/* Optional Row: Lead Score */}
                {lead?.leadScore != null && (
                    <div className="flex items-center justify-end gap-1.5 -mt-1">
                        <div className="w-14 h-1.5 bg-gray-100 dark:bg-dark-600 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full bg-gradient-to-r ${statusAccentBar}`}
                                style={{ width: `${Math.min(100, lead.leadScore)}%` }}
                            />
                        </div>
                        <span className="text-[11px] font-semibold text-content-subtle dark:text-gray-500 tabular-nums">{lead.leadScore}</span>
                    </div>
                )}

                {/* Row 3: Info pills — stacked on sm, side-by-side on lg+ */}
                <div className="pt-2.5 border-t border-line-subtle dark:border-dark-700">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-shrink-0 w-5 h-5 rounded-md bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center">
                                <Users className="w-3 h-3 text-violet-500 dark:text-violet-400" />
                            </div>
                            <span className="text-meta text-gray-700 dark:text-gray-300 truncate font-medium">{groupName}</span>
                        </div>
                        {email !== "—" && (
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="flex-shrink-0 w-5 h-5 rounded-md bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                                    <MailIcon className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                                </div>
                                <span className="text-meta text-content-secondary dark:text-gray-300 truncate">{email}</span>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5 mt-1.5">
                        {phoneNumber && (
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="flex-shrink-0 w-5 h-5 rounded-md bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                                    <Phone className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                                </div>
                                <span className="text-meta text-content-secondary dark:text-gray-400 truncate">{phoneNumber}</span>
                            </div>
                        )}
                        {whatsappNumber && (
                            <div className="flex items-center gap-2 min-w-0">
                                <div className="flex-shrink-0 w-5 h-5 rounded-md bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                                    <Phone className="w-3 h-3 text-green-500 dark:text-green-400" />
                                </div>
                                <span className="text-meta text-content-secondary dark:text-gray-400 truncate">{whatsappNumber}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer: Quick action buttons */}
            <div className="px-3 py-2.5 bg-gray-50 dark:bg-dark-700/40 border-t border-line-subtle dark:border-dark-700 flex items-center justify-between gap-1">
                <button
                    onClick={onEmail}
                    title="Send Email"
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-semibold text-content-secondary dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    <MailIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Email</span>
                </button>
                <div className="w-px h-4 bg-gray-200 dark:bg-dark-600 flex-shrink-0" />
                <button
                    onClick={() => navigate(`/leads/${lead?.groupId}/${lead?.group?.name}/${lead?.id}`)}
                    title="Edit Lead"
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-semibold text-content-secondary dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    <Edit className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Edit</span>
                </button>
                <div className="w-px h-4 bg-gray-200 dark:bg-dark-600 flex-shrink-0" />
                <button
                    onClick={() => { setSelectedCustomer(lead.id); setCustomerConfirmationOpen(true); }}
                    title="Delete Lead"
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[12px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Delete</span>
                </button>
            </div>
        </div>
    );
};
export default EnhancedLeadsView;
